import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ListChecks, RefreshCw, Package, AlertCircle, Loader, Check, X, Edit3, Trash2 } from 'lucide-react';
import { pickingService } from '../../../../services/pickingService';
import { userProfileService } from '../../../../services/userProfileService';
import { statusCodesService, StatusCodeDto } from '../../../../services/statusCodesService';
import { PListDto, PListDetailsDto } from '../../../../types/picking';
import { UserProfileDto } from '../../../../types/userProfile';
import { BackButton } from '../../../common/BackButton';
import { useWarehouse } from '../../../../contexts/WarehouseContext';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, ExpandableRowConfig, TableDataRow, RowAction } from '../../../shared/table';

// Extended type for table
interface PListTableRow extends TableDataRow {
  id: number;
  pListID: number;
  pListStatus: number;
  statusText: string;
  description1: string | null;
  description2: string | null;
  carrierName: string | null;
  pListDate: string | null;
  deliveryDate: string | null;
  numOfOrderLines: number;
  numOfOrderLinesPicked: number | null;
  weight: number | null;
  volum: number | null;
  totalVolume: number | null;
  totalWeight: number | null;
  operatorID: string | null;
  operatorGroupID: string | null;
  priority: number;
  pListIsBatchYesNo: boolean | null;
  filterGroupID: string | null;
  lockedBy: string | null;
  lockStatus: number;
  // Shipping
  numOfPackages: number | null;
  numOfPallets: number | null;
  packZoneID: string | null;
  packageRemark: string | null;
  shippingLabelsInitiated: boolean | null;
  shippingLabelsPrinted: boolean | null;
  // Signatures
  signature1Location: string | null;
  signature1DateTime: string | null;
  signature2Location: string | null;
  signature2DateTime: string | null;
  // Batch/Configuration
  packagingTypeNo: number | null;
  pListNumOfPickZoneSplits: number | null;
  deliveryMethodNo: number | null;
  clientID: string | null;
  sessionID: string | null;
  internalStatus: number | null;
  externalSystemID: number | null;
  allowAutoComplete: boolean | null;
  pListFinalizationLayout: number | null;
  approvePackageStatus: number | null;
  pListConfirmation: number | null;
  qtyDeviationControlTerminated: boolean | null;
  // Free fields
  pListFreeField1: string | null;
  pListFreeField2: string | null;
  pListFreeField3: string | null;
  pListFreeField4: string | null;
  pListFreeField5: string | null;
  pListFreeField6: string | null;
  pListFreeField7: string | null;
  pListFreeField8: string | null;
  pListFreeField9: string | null;
  pListFreeField10: string | null;
  // Work times
  startWorkDateTime: string | null;
  stopWorkDateTime: string | null;
  // Audit
  createdBy: string | null;
  created: string | null;
  updatedBy: string | null;
  lastUpdate: string | null;
  // Keep original for updates
  _original: PListDto;
}

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<PListTableRow>();

// StatusType for LockStatus in StatusCodes table
const LOCK_STATUS_TYPE = 2;

interface EditableCellState {
  [pListId: number]: {
    operatorID?: string;
    priority?: number;
    lockStatus?: number;
    lockedBy?: string;
  };
}

interface WorkingPickingListsProps {
  onBack?: () => void;
}

const WorkingPickingLists: React.FC<WorkingPickingListsProps> = ({ onBack }) => {
  const { currentWarehouse } = useWarehouse();
  const [pickingLists, setPickingLists] = useState<PListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Operators list for dropdown
  const [operators, setOperators] = useState<UserProfileDto[]>([]);
  const [loadingOperators, setLoadingOperators] = useState(false);

  // LockStatus options from StatusCodes (StatusType = 2)
  const [lockStatusOptions, setLockStatusOptions] = useState<StatusCodeDto[]>([]);

  // Expanded state and details
  const [listDetails, setListDetails] = useState<Map<number, PListDetailsDto>>(new Map());
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set());

  // Inline editing state
  const [editState, setEditState] = useState<EditableCellState>({});
  const [savingPLists, setSavingPLists] = useState<Set<number>>(new Set());
  const [savedPLists, setSavedPLists] = useState<Set<number>>(new Set());

  // Delete state
  const [deletingPListId, setDeletingPListId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pListToDelete, setPListToDelete] = useState<PListDto | null>(null);

  // Refs to access current state in stable callbacks
  const editStateRef = useRef<EditableCellState>(editState);
  const pickingListsRef = useRef<PListDto[]>(pickingLists);
  const listDetailsRef = useRef<Map<number, PListDetailsDto>>(listDetails);

  // Keep refs in sync with state
  useEffect(() => {
    editStateRef.current = editState;
  }, [editState]);

  useEffect(() => {
    pickingListsRef.current = pickingLists;
  }, [pickingLists]);

  useEffect(() => {
    listDetailsRef.current = listDetails;
  }, [listDetails]);

  useEffect(() => {
    loadPickingLists();
    loadOperators();
    loadLockStatusOptions();
  }, []);

  const loadOperators = async () => {
    try {
      setLoadingOperators(true);
      const profiles = await userProfileService.getAllProfilesForCompany();
      setOperators(profiles);
    } catch (err) {
      console.error('Error loading operators:', err);
    } finally {
      setLoadingOperators(false);
    }
  };

  const loadLockStatusOptions = async () => {
    try {
      const statusCodes = await statusCodesService.getByStatusType(LOCK_STATUS_TYPE);
      setLockStatusOptions(statusCodes);
    } catch (err) {
      console.error('Error loading lock status options:', err);
    }
  };

  const loadPickingLists = async () => {
    try {
      setLoading(true);
      setError(null);
      // Hent plukklister med statuser: 10, 20, 40, 45, 50
      const data = await pickingService.getPickingListsByStatuses([10, 20, 40, 45, 50], currentWarehouse || undefined);
      setPickingLists(data);

      // Refresh cached line details for currently expanded rows
      const cachedIds = Array.from(listDetailsRef.current.keys());
      if (cachedIds.length > 0) {
        const refreshed = await Promise.all(
          cachedIds.map(async (id) => {
            try {
              const details = await pickingService.getPickingDetails(id);
              return [id, details] as const;
            } catch (err) {
              console.error(`Error refreshing details for picking list ${id}:`, err);
              return null;
            }
          })
        );
        setListDetails(prev => {
          const next = new Map(prev);
          refreshed.forEach(entry => {
            if (entry) next.set(entry[0], entry[1]);
          });
          return next;
        });
      }

      // Initialize edit state
      const newEditState: EditableCellState = {};
      data.forEach(pl => {
        newEditState[pl.pListID] = {
          operatorID: pl.operatorID || '',
          priority: pl.priority
        };
      });
      setEditState(newEditState);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved lasting av plukklister');
      console.error('Error loading picking lists:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPickingListDetails = async (pListId: number) => {
    try {
      setLoadingDetails(prev => new Set(prev).add(pListId));
      const details = await pickingService.getPickingDetails(pListId);
      setListDetails(prev => new Map(prev).set(pListId, details));
    } catch (err) {
      console.error(`Error loading details for picking list ${pListId}:`, err);
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(pListId);
        return newSet;
      });
    }
  };

  // Transform to table data
  const tableData: PListTableRow[] = useMemo(() => {
    return pickingLists.map(pl => ({
      id: pl.pListID,
      pListID: pl.pListID,
      pListStatus: pl.pListStatus,
      statusText: pl.statusText,
      description1: pl.description1 || null,
      description2: pl.description2 || null,
      carrierName: pl.carrierName || null,
      pListDate: pl.pListDate || null,
      deliveryDate: pl.deliveryDate || null,
      numOfOrderLines: pl.numOfOrderLines || 0,
      numOfOrderLinesPicked: pl.numOfOrderLinesPicked ?? null,
      weight: pl.weight ?? null,
      volum: pl.volum ?? null,
      totalVolume: pl.totalVolume ?? null,
      totalWeight: pl.totalWeight ?? null,
      operatorID: pl.operatorID || null,
      operatorGroupID: pl.operatorGroupID || null,
      priority: pl.priority,
      pListIsBatchYesNo: pl.pListIsBatchYesNo ?? null,
      filterGroupID: pl.filterGroupID || null,
      lockedBy: pl.lockedBy || null,
      lockStatus: pl.lockStatus,
      // Shipping
      numOfPackages: pl.numOfPackages ?? null,
      numOfPallets: pl.numOfPallets ?? null,
      packZoneID: pl.packZoneID || null,
      packageRemark: pl.packageRemark || null,
      shippingLabelsInitiated: pl.shippingLabelsInitiated ?? null,
      shippingLabelsPrinted: pl.shippingLabelsPrinted ?? null,
      // Signatures
      signature1Location: pl.signature1Location || null,
      signature1DateTime: pl.signature1DateTime || null,
      signature2Location: pl.signature2Location || null,
      signature2DateTime: pl.signature2DateTime || null,
      // Batch/Configuration
      packagingTypeNo: pl.packagingTypeNo ?? null,
      pListNumOfPickZoneSplits: pl.pListNumOfPickZoneSplits ?? null,
      deliveryMethodNo: pl.deliveryMethodNo ?? null,
      clientID: pl.clientID || null,
      sessionID: pl.sessionID || null,
      internalStatus: pl.internalStatus ?? null,
      externalSystemID: pl.externalSystemID ?? null,
      allowAutoComplete: pl.allowAutoComplete ?? null,
      pListFinalizationLayout: pl.pListFinalizationLayout ?? null,
      approvePackageStatus: pl.approvePackageStatus ?? null,
      pListConfirmation: pl.pListConfirmation ?? null,
      qtyDeviationControlTerminated: pl.qtyDeviationControlTerminated ?? null,
      // Free fields
      pListFreeField1: pl.pListFreeField1 || null,
      pListFreeField2: pl.pListFreeField2 || null,
      pListFreeField3: pl.pListFreeField3 || null,
      pListFreeField4: pl.pListFreeField4 || null,
      pListFreeField5: pl.pListFreeField5 || null,
      pListFreeField6: pl.pListFreeField6 || null,
      pListFreeField7: pl.pListFreeField7 || null,
      pListFreeField8: pl.pListFreeField8 || null,
      pListFreeField9: pl.pListFreeField9 || null,
      pListFreeField10: pl.pListFreeField10 || null,
      // Work times
      startWorkDateTime: pl.startWorkDateTime || null,
      stopWorkDateTime: pl.stopWorkDateTime || null,
      // Audit
      createdBy: pl.createdBy || null,
      created: pl.created || null,
      updatedBy: pl.updatedBy || null,
      lastUpdate: pl.lastUpdate || null,
      _original: pl
    }));
  }, [pickingLists]);

  // Row ID function
  const getRowId = useCallback((row: PListTableRow) => row.pListID, []);

  // Save inline edit
  const saveInlineEdit = useCallback(async (pListId: number, field: 'operatorID' | 'priority' | 'lockStatus' | 'lockedBy', newFieldValue?: string | number) => {
    const originalPickList = pickingListsRef.current.find(p => p.pListID === pListId);
    if (!originalPickList) return;

    // Get value either from parameter or from editState ref
    const editData = editStateRef.current[pListId];
    let newValue: string | number | undefined;

    if (newFieldValue !== undefined) {
      // Value passed directly (from onChange)
      newValue = newFieldValue;
    } else if (editData) {
      // Value from editState ref (from onBlur)
      newValue = editData[field];
    } else {
      return;
    }

    // Check if the specific field changed
    let oldValue: string | number;
    switch (field) {
      case 'priority': oldValue = originalPickList.priority; break;
      case 'lockStatus': oldValue = originalPickList.lockStatus; break;
      case 'lockedBy': oldValue = originalPickList.lockedBy || ''; break;
      default: oldValue = originalPickList.operatorID || ''; break;
    }

    if (newValue === oldValue) {
      return; // No change
    }

    try {
      setSavingPLists(prev => new Set(prev).add(pListId));

      // Build complete PList object with updated fields (backend requires full object)
      const pickListUpdate: PListDto = {
        ...originalPickList,
        operatorID: field === 'operatorID' ? (newValue as string || undefined) : originalPickList.operatorID,
        priority: field === 'priority' ? (newValue as number) : originalPickList.priority,
        lockStatus: field === 'lockStatus' ? (newValue as number) : originalPickList.lockStatus,
        lockedBy: field === 'lockedBy' ? (newValue as string || undefined) : originalPickList.lockedBy
      };

      await pickingService.updatePickingList(pListId, pickListUpdate);

      // Update local state
      setPickingLists(prev => prev.map(p =>
        p.pListID === pListId ? { ...p, ...pickListUpdate } : p
      ));

      // Update editState to reflect saved value
      setEditState(prev => ({
        ...prev,
        [pListId]: {
          ...prev[pListId],
          operatorID: pickListUpdate.operatorID || '',
          priority: pickListUpdate.priority,
          lockStatus: pickListUpdate.lockStatus,
          lockedBy: pickListUpdate.lockedBy || ''
        }
      }));

      // Show saved indicator
      setSavedPLists(prev => new Set(prev).add(pListId));
      setTimeout(() => {
        setSavedPLists(prev => {
          const newSet = new Set(prev);
          newSet.delete(pListId);
          return newSet;
        });
      }, 2000);

    } catch (err) {
      console.error('Error saving:', err);
      alert(`Feil ved lagring: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setSavingPLists(prev => {
        const newSet = new Set(prev);
        newSet.delete(pListId);
        return newSet;
      });
    }
  }, []);

  // Inline editable OperatorID cell (dropdown) - uses key to force re-render after save
  const renderOperatorCell = useCallback((value: string | null, row: PListTableRow) => {
    const pListId = row.pListID;
    // Case-insensitive match: OperatorID i DB kan ha annen casing enn UserID i Profile-tabellen
    const matchedOperator = value
      ? operators.find(op => op.userID.toLowerCase() === value.toLowerCase())
      : null;
    const resolvedValue = matchedOperator?.userID || value || '';

    const stopAllPropagation = (e: React.SyntheticEvent) => {
      e.stopPropagation();
    };

    return (
      <div
        className="flex items-center space-x-1"
        onClick={stopAllPropagation}
        onMouseDown={stopAllPropagation}
      >
        <select
          key={`operator-select-${pListId}-${resolvedValue}-${operators.length}`}
          defaultValue={resolvedValue}
          onChange={(e) => {
            e.stopPropagation();
            const newValue = e.target.value;
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], operatorID: newValue }
            }));
            // Save immediately on change, passing value directly
            saveInlineEdit(pListId, 'operatorID', newValue);
          }}
          onClick={stopAllPropagation}
          onMouseDown={stopAllPropagation}
          onFocus={stopAllPropagation}
          className="w-28 px-2 py-1 text-xs border border-gray-300 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white"
        >
          <option value="">-</option>
          {operators.map(op => (
            <option key={op.userID} value={op.userID}>
              {op.profileName || op.userID}
            </option>
          ))}
        </select>
      </div>
    );
  }, [operators, saveInlineEdit]);

  // Inline editable Priority cell (number input) - uses uncontrolled input like NewPickingLists
  const renderPriorityCell = useCallback((value: number, row: PListTableRow) => {
    const pListId = row.pListID;

    const stopAllPropagation = (e: React.SyntheticEvent) => {
      e.stopPropagation();
    };

    return (
      <div
        className="flex items-center space-x-1"
        onClick={stopAllPropagation}
        onMouseDown={stopAllPropagation}
      >
        <input
          key={`priority-input-${pListId}-${value}`}
          type="number"
          defaultValue={value}
          min={0}
          max={999}
          onChange={(e) => {
            e.stopPropagation();
            const newValue = parseInt(e.target.value) || 0;
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], priority: newValue }
            }));
          }}
          onBlur={() => saveInlineEdit(pListId, 'priority')}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              saveInlineEdit(pListId, 'priority');
              (e.target as HTMLInputElement).blur();
            }
          }}
          onClick={stopAllPropagation}
          onMouseDown={stopAllPropagation}
          onFocus={stopAllPropagation}
          className="w-16 px-2 py-1 text-xs text-right border border-gray-300 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white"
        />
      </div>
    );
  }, [saveInlineEdit]);

  // Inline editable LockStatus cell (dropdown) - uses lockStatusOptions from StatusCodes
  const renderLockStatusCell = useCallback((value: number, row: PListTableRow) => {
    const pListId = row.pListID;

    const stopAllPropagation = (e: React.SyntheticEvent) => {
      e.stopPropagation();
    };

    return (
      <div
        className="flex items-center space-x-1"
        onClick={stopAllPropagation}
        onMouseDown={stopAllPropagation}
      >
        <select
          key={`lockstatus-select-${pListId}-${value}-${lockStatusOptions.length}`}
          defaultValue={value}
          onChange={(e) => {
            e.stopPropagation();
            const newValue = parseInt(e.target.value) || 0;
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], lockStatus: newValue }
            }));
            saveInlineEdit(pListId, 'lockStatus', newValue);
          }}
          onClick={stopAllPropagation}
          onMouseDown={stopAllPropagation}
          onFocus={stopAllPropagation}
          className="w-28 px-2 py-1 text-xs border border-gray-300 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white"
        >
          {lockStatusOptions.map(opt => (
            <option key={opt.statusCode} value={opt.statusCode}>
              {opt.lng_1 || opt.statusCode.toString()}
            </option>
          ))}
        </select>
      </div>
    );
  }, [lockStatusOptions, saveInlineEdit]);

  // Inline editable LockedBy cell (dropdown) - uses operators list
  const renderLockedByCell = useCallback((value: string | null, row: PListTableRow) => {
    const pListId = row.pListID;
    // Case-insensitive match: LockedBy i DB kan ha annen casing enn UserID i Profile-tabellen
    const matchedOperator = value
      ? operators.find(op => op.userID.toLowerCase() === value.toLowerCase())
      : null;
    const resolvedValue = matchedOperator?.userID || value || '';

    const stopAllPropagation = (e: React.SyntheticEvent) => {
      e.stopPropagation();
    };

    return (
      <div
        className="flex items-center space-x-1"
        onClick={stopAllPropagation}
        onMouseDown={stopAllPropagation}
      >
        <select
          key={`lockedby-select-${pListId}-${resolvedValue}-${operators.length}`}
          defaultValue={resolvedValue}
          onChange={(e) => {
            e.stopPropagation();
            const newValue = e.target.value;
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], lockedBy: newValue }
            }));
            saveInlineEdit(pListId, 'lockedBy', newValue);
          }}
          onClick={stopAllPropagation}
          onMouseDown={stopAllPropagation}
          onFocus={stopAllPropagation}
          className="w-28 px-2 py-1 text-xs border border-gray-300 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white"
        >
          <option value="">-</option>
          {operators.map(op => (
            <option key={op.userID} value={op.userID}>
              {op.profileName || op.userID}
            </option>
          ))}
        </select>
      </div>
    );
  }, [operators, saveInlineEdit]);

  // Row actions (Handling column)
  const rowActions: RowAction<PListTableRow>[] = useMemo(() => [
    {
      id: 'delete-plist',
      label: 'Slett plukkliste',
      icon: Trash2,
      variant: 'danger' as const,
      disabled: (row: PListTableRow) =>
        row.pListStatus > 20 ||
        !!row.lockedBy ||
        (row.numOfOrderLinesPicked ?? 0) > 0,
      onClick: (row: PListTableRow) => {
        setPListToDelete(row._original);
        setShowDeleteConfirm(true);
      }
    }
  ], []);

  const getStatusColor = (status: number): string => {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-800';
      case 10: return 'bg-green-100 text-green-800';
      case 20: return 'bg-blue-100 text-blue-800';
      case 30: return 'bg-yellow-100 text-yellow-800';
      case 40: return 'bg-green-100 text-green-800';
      case 45: return 'bg-purple-100 text-purple-800';
      case 50: return 'bg-indigo-100 text-indigo-800';
      case 60: return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Define columns based on legacy PListOptimized.dfm - all database fields available
  const columns: ColumnDefinition<PListTableRow>[] = useMemo(() => [
    // === HOVEDFELTER (synlige som standard) ===
    columnFactory.createNumberColumn('pListID', 'pListID', 'Plukklistenr.', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-'
    }),
    columnFactory.createTextColumn('statusText', 'statusText', 'Status', {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      renderer: (value, row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.pListStatus)}`}>
          {value}
        </span>
      )
    }),
    // Editable: LockedBy
    columnFactory.createTextColumn('lockedBy', 'lockedBy', 'Låst av', {
      width: 130,
      sortable: true,
      filterType: 'both',
      renderer: renderLockedByCell
    }),
    // Editable: LockStatus
    columnFactory.createNumberColumn('lockStatus', 'lockStatus', 'Systemstatus', {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      renderer: renderLockStatusCell
    }),
    columnFactory.createTextColumn('description1', 'description1', 'Beskrivelse', {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('carrierName', 'carrierName', 'Transportør', {
      width: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createDateColumn('pListDate', 'pListDate', 'Opprettet dato', {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createDateColumn('deliveryDate', 'deliveryDate', 'Leveringsdato', {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('numOfOrderLines', 'numOfOrderLines', 'Ant. linjer', {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('numOfOrderLinesPicked', 'numOfOrderLinesPicked', 'Ant. plukket', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-'
    }),
    columnFactory.createNumberColumn('weight', 'weight', 'Vekt', {
      width: 80,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? `${value.toFixed(2)} kg` : '-'
    }),
    // Editable: OperatorID
    columnFactory.createTextColumn('operatorID', 'operatorID', 'Operatør', {
      width: 130,
      sortable: true,
      filterType: 'both',
      renderer: renderOperatorCell
    }),
    // Editable: Priority
    columnFactory.createNumberColumn('priority', 'priority', 'Prioritet', {
      width: 80,
      sortable: true,
      filterType: 'both',
      renderer: renderPriorityCell
    }),

    // === BESKRIVELSE (skjult som standard) ===
    columnFactory.createTextColumn('description2', 'description2', 'Beskrivelse 2', {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === VOLUM OG VEKT (skjult som standard) ===
    columnFactory.createNumberColumn('volum', 'volum', 'Volum (enhet)', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(2) : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('totalVolume', 'totalVolume', 'Total volum', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(2) : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('totalWeight', 'totalWeight', 'Total vekt', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? `${value.toFixed(2)} kg` : '-',
      visible: false
    }),

    // === OPERATØR (skjult som standard) ===
    columnFactory.createTextColumn('operatorGroupID', 'operatorGroupID', 'Operatørgruppe', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === BATCH/FILTER (skjult som standard) ===
    columnFactory.createTextColumn('pListIsBatchYesNo', 'pListIsBatchYesNo', 'Samplukk', {
      width: 90,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),
    columnFactory.createTextColumn('filterGroupID', 'filterGroupID', 'Filtergruppe', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === PAKKING/SHIPPING (skjult som standard) ===
    columnFactory.createNumberColumn('numOfPackages', 'numOfPackages', 'Ant. kolli', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('numOfPallets', 'numOfPallets', 'Ant. paller', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(1) : '-',
      visible: false
    }),
    columnFactory.createTextColumn('packZoneID', 'packZoneID', 'Lastesone', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('packageRemark', 'packageRemark', 'Pakkekommentar', {
      width: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('shippingLabelsInitiated', 'shippingLabelsInitiated', 'Fraktmerker init.', {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),
    columnFactory.createTextColumn('shippingLabelsPrinted', 'shippingLabelsPrinted', 'Fraktmerker skrevet', {
      width: 120,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),

    // === SIGNATURER (skjult som standard) ===
    columnFactory.createTextColumn('signature1Location', 'signature1Location', 'Signatur 1 sted', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createDateColumn('signature1DateTime', 'signature1DateTime', 'Signatur 1 tid', {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('signature2Location', 'signature2Location', 'Signatur 2 sted', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createDateColumn('signature2DateTime', 'signature2DateTime', 'Signatur 2 tid', {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // === ARBEIDSTIDER (skjult som standard) ===
    columnFactory.createDateColumn('startWorkDateTime', 'startWorkDateTime', 'Arbeid startet', {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('stopWorkDateTime', 'stopWorkDateTime', 'Arbeid stoppet', {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // === KONFIGURASJON (skjult som standard) ===
    columnFactory.createNumberColumn('packagingTypeNo', 'packagingTypeNo', 'Emballasjetype', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('pListNumOfPickZoneSplits', 'pListNumOfPickZoneSplits', 'Plukksonesplitter', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('deliveryMethodNo', 'deliveryMethodNo', 'Leveringsmetode', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createTextColumn('allowAutoComplete', 'allowAutoComplete', 'Autofullføring', {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('pListFinalizationLayout', 'pListFinalizationLayout', 'Ferdigstillelseslayout', {
      width: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('approvePackageStatus', 'approvePackageStatus', 'Godkjenn pakkestatus', {
      width: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('pListConfirmation', 'pListConfirmation', 'Bekreftelse', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createTextColumn('qtyDeviationControlTerminated', 'qtyDeviationControlTerminated', 'Avvikskontroll avsl.', {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),

    // === KLIENT/SESJON (skjult som standard) ===
    columnFactory.createTextColumn('clientID', 'clientID', 'Klient-ID', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('sessionID', 'sessionID', 'Sesjons-ID', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createNumberColumn('internalStatus', 'internalStatus', 'Intern status', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('externalSystemID', 'externalSystemID', 'Eksternt system', {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),

    // === FRIE FELT (skjult som standard) ===
    columnFactory.createTextColumn('pListFreeField1', 'pListFreeField1', 'Fritt felt 1', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField2', 'pListFreeField2', 'Fritt felt 2', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField3', 'pListFreeField3', 'Fritt felt 3', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField4', 'pListFreeField4', 'Fritt felt 4', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField5', 'pListFreeField5', 'Fritt felt 5', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField6', 'pListFreeField6', 'Fritt felt 6', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField7', 'pListFreeField7', 'Fritt felt 7', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField8', 'pListFreeField8', 'Fritt felt 8', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField9', 'pListFreeField9', 'Fritt felt 9', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField10', 'pListFreeField10', 'Fritt felt 10', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === AUDIT (skjult som standard) ===
    columnFactory.createTextColumn('createdBy', 'createdBy', 'Opprettet av', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createDateColumn('created', 'created', 'Opprettet', {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', 'Oppdatert av', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', 'Sist oppdatert', {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

  ], [lockStatusOptions, renderOperatorCell, renderPriorityCell, renderLockStatusCell, renderLockedByCell]);

  // Delete handlers
  const handleConfirmDelete = async () => {
    if (!pListToDelete) return;

    try {
      setDeletingPListId(pListToDelete.pListID);
      setShowDeleteConfirm(false);

      const result = await pickingService.deletePickingList(pListToDelete.pListID);

      if (result.success) {
        setPickingLists(prev => prev.filter(p => p.pListID !== pListToDelete.pListID));
        alert(result.message);
      }
    } catch (err) {
      console.error('Error deleting PList:', err);
      alert(`Feil ved sletting: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setDeletingPListId(null);
      setPListToDelete(null);
    }
  };

  // Detail renderer for expandable rows
  const renderPListLines = useCallback((pList: PListTableRow, details: PListDetailsDto | undefined) => {
    const isLoadingDetails = loadingDetails.has(pList.pListID);

    if (isLoadingDetails) {
      return (
        <div className="flex items-center justify-center py-8 bg-slate-50">
          <Loader className="animate-spin mr-2" size={20} />
          <span className="text-gray-600">Laster linjer...</span>
        </div>
      );
    }

    if (!details) {
      return (
        <div className="text-center py-8 text-gray-500 bg-slate-50">
          Kunne ikke laste linjer
        </div>
      );
    }

    return (
      <div className="bg-slate-50 p-3 border-t border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <Package size={16} />
          <span>Plukkliste-linjer for {pList.pListID}</span>
          <span className="text-xs text-gray-500">
            ({details.completedLines} av {details.totalLines} fullført - {details.completionPercentage.toFixed(0)}%)
          </span>
        </h4>

        {details.lines.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100">
                <th className="text-left py-2 px-3 font-medium text-slate-500">Linjenr</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Ordrenr</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Ordre linje</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Varenr</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">Antall</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">Reservert</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">Plukket</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">Gjenstående</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Lokasjon</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Lager</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Status</th>
                <th className="text-center py-2 px-3 font-medium text-slate-500">Fullført</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {details.lines.map((line) => (
                <tr key={line.id} className="hover:bg-slate-100 transition-colors">
                  <td className="py-2 px-3 text-slate-900 font-medium">{line.pListLineNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.orderNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.lineNo}</td>
                  <td className="py-2 px-3 text-slate-900 font-mono">{line.itemNo || '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qty.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyReserved.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyPicked.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right font-medium">{line.remainingQty.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900">{line.binCode || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.warehouseID || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.lineStatus}</td>
                  <td className="py-2 px-3 text-center">
                    {line.isCompleted ? (
                      <Check className="text-green-600 mx-auto" size={16} />
                    ) : (
                      <X className="text-gray-400 mx-auto" size={16} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Ingen linjer funnet for denne plukklisten
          </div>
        )}
      </div>
    );
  }, [loadingDetails]);

  // Expandable configuration
  const expandableConfig: ExpandableRowConfig<PListTableRow, PListDetailsDto | undefined> = useMemo(() => ({
    getDetailData: (pList) => listDetails.get(pList.pListID),
    renderDetail: renderPListLines,
    expandOnRowClick: false,
    onExpand: async (pList) => {
      if (!listDetails.has(pList.pListID)) {
        await loadPickingListDetails(pList.pListID);
      }
    }
  }), [listDetails, renderPListLines]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: 'Oppdater',
      icon: RefreshCw,
      onClick: loadPickingLists,
      variant: 'secondary' as const
    }
  ], []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>Laster plukklister...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <h3 className="text-red-800 font-medium">Feil ved lasting av plukklister</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadPickingLists}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>Prøv igjen</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <ListChecks size={24} />
            <span>Plukklister under arbeid</span>
          </h1>
          <span className="text-sm text-gray-500">
            {tableData.length} plukklister
          </span>
        </div>
        <p className="text-gray-500 text-sm mt-1 ml-12">
          <Edit3 size={12} className="inline mr-1" />
          Operatør og Prioritet er redigerbare
        </p>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 px-4 py-2">
        <WarehouseTable
          data={tableData}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'working-picking-lists-table'
          }}
          getRowId={getRowId}
          rowActions={rowActions}
          tableActions={tableActions}
          expandableConfig={expandableConfig}
          loading={false}
          error={undefined}
          aria-label="Plukklister under arbeid tabell"
        />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && pListToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg flex items-center space-x-3">
              <Trash2 size={24} />
              <h2 className="text-xl font-semibold">Bekreft sletting</h2>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Er du sikker på at du vil slette plukkliste <strong>{pListToDelete.pListID}</strong>?
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Beskrivelse:</div>
                  <div className="text-gray-900">{pListToDelete.description1 || '-'}</div>
                  <div className="text-gray-500">Antall linjer:</div>
                  <div className="text-gray-900">{pListToDelete.numOfOrderLines || 0}</div>
                  <div className="text-gray-500">Status:</div>
                  <div className="text-gray-900">{pListToDelete.statusText}</div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>Advarsel:</strong> Denne handlingen kan ikke angres. Plukklisten og alle tilhørende linjer vil bli permanent slettet.
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPListToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Avbryt
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2"
              >
                <Trash2 size={16} />
                <span>Slett plukkliste</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkingPickingLists;
